import { gql, useQuery } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import React, { useContext, useEffect, useMemo, useRef } from 'react';

import type { GraphLoginAsync } from '../../../graphdebate/src/components/auth/LoadGraphLogin';
import type { AuthBan } from '../contributor/Ban/ban-models';
import { getHandledBan } from '../contributor/Ban/ban-utils';
import { useAppDispatch } from '../redux/hooks';
import { logoutActionType } from '../redux/redux.utils';
import { setMe } from '../redux/slices/utils-slice';
import { userQuery } from '../services/graphql-queries';
import type { Ban, Profile, QueryType, User } from '../types/api';
import { useConnectedRouter } from './useConnectedRouter';
import { readIsGuest, readStatus } from './usePermissions-utils';
import { useWebsite } from './useWebsite';

export const CheckVIPQuery = gql`
  query CheckVIPQuery {
    me {
      id
      profile {
        id
        status
        isVip
      }
    }
    website {
      id
      subscribeByTokenVip
    }
  }
`;

interface AuthMethods {
  graphLogin: GraphLoginAsync;
}
export interface AuthPermissions extends GraphLoginAsync {
  isSuperAdmin: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  isRedactor: boolean;
  isVip: boolean;
  authUserId: string;
  currentBan?: AuthBan;
  isAuthDone: boolean;
  isDeleted: boolean;
  email?: string;
  username?: string;
  language: string;
  profile: Profile | undefined;
  forceRefetchUser: () => Promise<void>;
}
export const authContext = React.createContext<AuthPermissions>(null as any);

// For the error reporting only. Don't use in the app because it is unsafe.
export let _authUserId: string | undefined = undefined;

export const AuthProvider: React.FC<PropsWithChildren<AuthMethods>> = ({
  children,
  graphLogin,
}) => {
  // Risk with the cache enabled: this auth query used to cause issues with auth status sometimes incorrect when refreshing the page. Seen on localhost, I don't know if we have the issue once deployed.
  const { data, called, loading, refetch } = useQuery<QueryType>(userQuery, {
    fetchPolicy: 'network-only',
    // fetchPolicy: 'cache-and-network',
    // fetchPolicy: 'cache-first',
  });
  const website = useWebsite();

  const { getToken, fetchGraphLogin } = graphLogin;

  // Track if we've already attempted a refresh to prevent infinite loops.
  // This handles the case where the API returns me:null for an expired token (instead of throwing an error).
  // See docs/REFRESH_TOKEN.md for detailed explanation of the refresh token flow.
  const hasTriedRefreshRef = useRef(false);

  const me = data?.me ?? {};
  const { id: authUserId, delete: isDeleted, email, username, language, profile } = me;
  _authUserId = authUserId;
  const isVip = data?.me?.profile?.isVip;
  const currentBan = getHandledBan(profile?.isBan as Ban[]);
  const { isSuperAdmin, isOwner, isAdmin, isModerator, isRedactor } = readStatus(me, website);
  const isAuthDone = called && !loading;

  // Handle the case where API returns me:null for an expired token (security design decision).
  // The API doesn't throw an error for expired tokens on the `me` query, so errorLink cannot intercept it.
  // We detect this case here and manually trigger a refresh + refetch.
  // Note: Native apps have their own refresh mechanism in nativeSessionManager.ts that runs earlier,
  // but this hook serves as a fallback and also handles the web case.
  useEffect(() => {
    // Reset the flag when user is authenticated (successful login or refresh)
    if (authUserId) {
      hasTriedRefreshRef.current = false;
      return;
    }

    // Only proceed if query is done loading and we haven't already tried refreshing
    if (!isAuthDone || hasTriedRefreshRef.current) return;

    (async () => {
      try {
        const token = await getToken();

        // If we have a token but no user, the token is likely expired.
        // The API returns me:null instead of throwing an error (security decision).
        // We need to manually trigger a refresh and refetch.
        if (token) {
          console.log('🔐 ~ Token present but me is null');
          console.log('🔄 ~ Triggering refresh...');
          hasTriedRefreshRef.current = true;

          await fetchGraphLogin('triggerRefreshToken');

          // Wait for the new token to be propagated to storage.
          // This delay accounts for postMessage communication with the GraphLogin iframe
          // and cookie/storage writes. Safari may require slightly more time due to
          // third-party storage partitioning.
          await new Promise(resolve => setTimeout(resolve, 200));

          // Refetch userQuery with the new token
          await refetch();
        }
      } catch (error) {
        console.error('[Auth] Failed to refresh token:', error);
      }
    })();
  }, [isAuthDone, authUserId, getToken, fetchGraphLogin, refetch]);

  const dispatch = useAppDispatch();
  const me1 = useMemo(
    () =>
      ({
        isSuperAdmin,
        isOwner,
        isAdmin,
        isModerator,
        isRedactor,
        isVip: !!isVip,
        authUserId: authUserId!,
        isAuthDone,
        currentBan,
        email,
        username,
        isDeleted,
        language,
        profile,
      } as AuthPermissions),
    [
      authUserId,
      email,
      isAdmin,
      isAuthDone,
      currentBan,
      isDeleted,
      isModerator,
      isOwner,
      isRedactor,
      isSuperAdmin,
      isVip,
      language,
      profile,
      username,
    ],
  );
  const value = useMemo(() => {
    const { logout, ...rest } = graphLogin;
    const me2: AuthPermissions = {
      ...me1,
      logout: async () => {
        if (logout) {
          dispatch({ type: logoutActionType });
          await logout();
        }
      },
      forceRefetchUser: async () => {
        await refetch();
      },
      ...rest,
    };
    return me2;
  }, [graphLogin, me1, dispatch, refetch]);

  useEffect(() => {
    dispatch(setMe(me1));
  }, [dispatch, me1]);

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};

// similar to "me" returned by graphql
export const usePermissions = (forceRedirect = false) => {
  const { push } = useConnectedRouter();
  const auth = useContext(authContext);

  if (auth == null) {
    throw new Error('usePermissions must be used within the AuthProvider.');
  }

  useEffect(() => {
    // In case user is deleted, go back home
    if (forceRedirect && auth?.isDeleted) push('/');
  }, [forceRedirect, auth?.isDeleted, push]);

  return auth;
};

export const useMe = () => {
  const { authUserId, isDeleted, ...others } = usePermissions();
  return useMemo(() => {
    const me: User = {
      id: authUserId,
      delete: isDeleted,
      ...others,
    };
    return me;
  }, [authUserId, isDeleted, others]);
};

// Always reloaded on the client, because it is highly dynamic (the user signs in and out).
export const useAuthProfile = () => {
  let { profile, authUserId, isAuthDone } = useContext(authContext);
  if (!profile) {
    profile = {};
  }

  const isGuest = useMemo(
    () => readIsGuest(authUserId, isAuthDone, profile?.id),
    [authUserId, isAuthDone, profile?.id],
  );

  const hasProfile = useMemo(
    () => isAuthDone && authUserId && !!profile?.pseudo && profile?.status !== 'deleted',
    [profile, isAuthDone, authUserId],
  );
  return { profile, isGuest, hasProfile, loading: !isAuthDone };
};

export function useIsMine(author: User | undefined) {
  const { authUserId } = usePermissions();
  const isMine = useMemo(() => !!authUserId && authUserId === author?.id, [authUserId, author?.id]);
  return isMine;
}
